import * as PIXI from "pixi.js";
import { useLayoutEffect, useRef } from "react";
import { OpaqueRoot } from "react-reconciler";
import { ConcurrentRoot } from "react-reconciler/constants";
import ReactGraphicsContext from "./ReactGraphicsContext";
import reconciler from "./reconciler";

type Props = {
  width: number;
  height: number;
  children?: React.ReactNode;
};

const MAX_PIXELS = 2560 * 1440;

const calculateResolution = (
  width: number,
  height: number,
  devicePixelRatio: number
) => {
  return Math.min(devicePixelRatio, Math.sqrt(MAX_PIXELS / (width * height)));
};

const Canvas = ({ width, height, children }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const appAndContainer = useRef<{
    pixiApp: PIXI.Application;
    reactContainer: OpaqueRoot;
  } | null>(null);
  useLayoutEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    if (!appAndContainer.current) {
      const pixiApp = new PIXI.Application({
        width,
        height,
        antialias: true,
        resolution: calculateResolution(width, height, window.devicePixelRatio),
        view: canvasRef.current,
        backgroundAlpha: 0,
      });
      const reactContainer = reconciler.createContainer(
        pixiApp,
        ConcurrentRoot,
        null,
        false,
        null,
        "",
        () => {},
        null
      );
      appAndContainer.current = { pixiApp, reactContainer };
    }

    appAndContainer.current.pixiApp.renderer.resize(
      window.innerWidth,
      window.innerHeight
    );

    reconciler.updateContainer(
      <ReactGraphicsContext.Provider
        value={{ pixiApp: appAndContainer.current.pixiApp }}
      >
        {children}
      </ReactGraphicsContext.Provider>,
      appAndContainer.current.reactContainer,
      null,
      () => {}
    );
  }, [width, height, children]);
  return (
    <canvas
      ref={canvasRef}
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
};

export default Canvas;
